import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import type { MarkupRule } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { Drawer } from '../components/ui/Drawer'
import { GlowCard } from '../components/ui/GlowCard'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

const APPLIES_TO = ['global', 'category', 'manufacturer'] as const
const MARKUP_TYPES = ['multiplier', 'fixed_add', 'percent'] as const

export default function MarkupRules() {
  const [rules, setRules] = useState<MarkupRule[]>([])
  const [loading, setLoading] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editRule, setEditRule] = useState<MarkupRule | null>(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    applies_to: 'global' as string,
    applies_to_value: '',
    markup_type: 'multiplier' as string,
    markup_value: '',
    priority: '0',
  })

  const loadRules = useCallback(async () => {
    setLoading(true)
    try {
      const result = await api.get<MarkupRule[]>('/markup-rules')
      setRules(result)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadRules() }, [loadRules])

  function openCreate() {
    setEditRule(null)
    setFormData({ name: '', applies_to: 'global', applies_to_value: '', markup_type: 'multiplier', markup_value: '', priority: '0' })
    setDrawerOpen(true)
  }

  function openEdit(rule: MarkupRule) {
    setEditRule(rule)
    setFormData({
      name: rule.name,
      applies_to: rule.applies_to,
      applies_to_value: rule.applies_to_value ?? '',
      markup_type: rule.markup_type,
      markup_value: String(rule.markup_value),
      priority: String(rule.priority),
    })
    setDrawerOpen(true)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        name: formData.name,
        applies_to: formData.applies_to,
        applies_to_value: formData.applies_to_value || undefined,
        markup_type: formData.markup_type,
        markup_value: parseFloat(formData.markup_value) || 0,
        priority: parseInt(formData.priority) || 0,
      }
      if (editRule) {
        await api.patch(`/markup-rules/${editRule.slug}`, payload)
      } else {
        await api.post('/markup-rules', payload)
      }
      setDrawerOpen(false)
      setEditRule(null)
      loadRules()
    } catch { /* handled */ }
    finally { setSaving(false) }
  }

  async function toggleActive(rule: MarkupRule) {
    try {
      await api.patch(`/markup-rules/${rule.slug}`, { is_active: rule.is_active ? 0 : 1 })
      loadRules()
    } catch { /* handled */ }
  }

  async function deleteRule(slug: string) {
    try {
      await api.delete(`/markup-rules/${slug}`)
      loadRules()
    } catch { /* handled */ }
  }

  function formatMarkup(rule: MarkupRule) {
    switch (rule.markup_type) {
      case 'multiplier': return `${rule.markup_value}x`
      case 'percent': return `${rule.markup_value}%`
      case 'fixed_add': return `+$${rule.markup_value}`
      default: return String(rule.markup_value)
    }
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Markup Rules</h1>
        <Button onClick={openCreate} icon={PlusIcon}>
          <span className="hidden sm:inline">Add Rule</span>
          <span className="sm:hidden">Add</span>
        </Button>
      </div>

      {loading ? (
        <TableSkeleton />
      ) : rules.length === 0 ? (
        <EmptyState
          title="No markup rules"
          description="Create pricing markup rules to automatically calculate sell prices from costs."
          action={<Button onClick={openCreate}>Add Rule</Button>}
        />
      ) : (
        <GlowCard style={{ padding: 0 }}>
          <table className="v3-table">
            <thead>
              <tr>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-left">Name</th>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-left">Applies To</th>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-left">Type</th>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right">Value</th>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right">Priority</th>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-center">Active</th>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.sort((a, b) => b.priority - a.priority).map((rule) => (
                <tr key={rule.id} className={!rule.is_active ? 'opacity-50' : ''}>
                  <td style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{rule.name}</td>
                  <td>
                    <Badge v="info">{rule.applies_to}</Badge>
                    {rule.applies_to_value && (
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 4 }}>({rule.applies_to_value})</span>
                    )}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-secondary)' }} className="capitalize">{rule.markup_type.replace(/_/g, ' ')}</td>
                  <td className="text-right mono" style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{formatMarkup(rule)}</td>
                  <td className="text-right mono" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{rule.priority}</td>
                  <td className="text-center">
                    <button
                      onClick={() => toggleActive(rule)}
                      className="w-8 h-4 rounded-full relative transition-colors"
                      style={{ background: rule.is_active ? 'var(--success)' : 'var(--border)' }}
                    >
                      <span
                        className="absolute top-0.5 w-3 h-3 rounded-full transition-transform"
                        style={{ background: '#fff', ...(rule.is_active ? { right: 2 } : { left: 2 }) }}
                      />
                    </button>
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(rule)} style={{ fontSize: 12, color: 'var(--primary)' }} className="hover:underline">Edit</button>
                      <span style={{ color: 'var(--text-tertiary)' }}>·</span>
                      <button onClick={() => deleteRule(rule.slug)} style={{ fontSize: 12, color: 'var(--error)' }} className="hover:underline">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlowCard>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => { setDrawerOpen(false); setEditRule(null) }} title={editRule ? 'Edit Rule' : 'Add Markup Rule'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Name *</label>
            <input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
              placeholder="e.g. Standard Markup"
              style={{ fontSize: 14 }}
            />
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Applies To</label>
            <select
              value={formData.applies_to}
              onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })}
              className="input"
              style={{ fontSize: 14 }}
            >
              {APPLIES_TO.map((a) => (
                <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
              ))}
            </select>
          </div>
          {formData.applies_to !== 'global' && (
            <div>
              <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formData.applies_to === 'category' ? 'Category Value' : 'Manufacturer Value'}</label>
              <input
                value={formData.applies_to_value}
                onChange={(e) => setFormData({ ...formData, applies_to_value: e.target.value })}
                className="input"
                placeholder={formData.applies_to === 'category' ? 'e.g. cameras' : 'e.g. Hikvision'}
                style={{ fontSize: 14 }}
              />
            </div>
          )}
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Markup Type</label>
            <select
              value={formData.markup_type}
              onChange={(e) => setFormData({ ...formData, markup_type: e.target.value })}
              className="input"
              style={{ fontSize: 14 }}
            >
              {MARKUP_TYPES.map((t) => (
                <option key={t} value={t}>{t === 'multiplier' ? 'Multiplier (e.g. 1.5x)' : t === 'percent' ? 'Percent (e.g. 50%)' : 'Fixed Add (e.g. +$100)'}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Markup Value *</label>
            <input
              type="number"
              step="0.01"
              value={formData.markup_value}
              onChange={(e) => setFormData({ ...formData, markup_value: e.target.value })}
              className="input"
              required
              placeholder={formData.markup_type === 'multiplier' ? '1.5' : formData.markup_type === 'percent' ? '50' : '100'}
              style={{ fontSize: 14 }}
            />
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Priority</label>
            <input
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="input"
              style={{ fontSize: 14 }}
            />
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Higher priority rules are applied first.</p>
          </div>
          <Button type="submit" loading={saving} className="w-full">
            {editRule ? 'Save Changes' : 'Create Rule'}
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
